import { useState } from 'react';
import type { FormEvent } from 'react';
import { X } from 'lucide-react';
import { OPS_PASSWORD_MAX_LENGTH, OPS_PASSWORD_MIN_LENGTH } from '@nadoutong/contracts';
import { OpsApiError, opsApiRequest } from '../api/client.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { Button, FormField, IconButton, Input } from '../ui/index.ts';
import { Modal } from './Modal.tsx';
import { useToast, coerceErrorMessage } from './Toast.tsx';

/**
 * Self-service password change for the signed-in operator.
 *
 * There is deliberately no user picker: ops-api resolves the target from the
 * session cookie and ignores any id in the body, so this modal can only ever
 * change the password of whoever is looking at it. Knowledge of the current
 * password is the authorization — an unattended logged-in browser is not
 * enough to lock the real owner out.
 *
 * A success destroys every session for the account, including this one, so
 * the only correct next step is to drop the client-side mirror and let the
 * app fall back to the login screen. Rendering anything else here would be a
 * UI that is already signed out.
 */
export function ChangePasswordModal({ onClose }: { readonly onClose: () => void }): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { fetchImpl, clearUser } = useSession();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (next.length < OPS_PASSWORD_MIN_LENGTH || next.length > OPS_PASSWORD_MAX_LENGTH) {
      setError(
        t('change_password.error.length', {
          min: String(OPS_PASSWORD_MIN_LENGTH),
          max: String(OPS_PASSWORD_MAX_LENGTH),
        }),
      );
      return;
    }
    if (next !== confirm) {
      setError(t('change_password.error.mismatch'));
      return;
    }
    if (next === current) {
      setError(t('change_password.error.same'));
      return;
    }
    setSubmitting(true);
    try {
      await opsApiRequest<{ ok: true; sessions_invalidated: number }>(
        '/v1/auth/change-password',
        { method: 'POST', body: { current_password: current, new_password: next } },
        fetchImpl,
      );
      toast.success(t('change_password.toast.success'));
      // Every session is gone server-side; keeping the mirror would render a
      // signed-in shell whose next request 401s.
      clearUser();
      onClose();
    } catch (err) {
      const message =
        err instanceof OpsApiError ? `${err.code}: ${err.message}` : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId="ops-change-password-title"
      testId="ops-change-password-modal"
      closeOnBackdrop={!submitting}
      panelClassName="max-w-xl rounded-xl"
    >
      <form className="p-6" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="m-0 text-label font-medium text-paper-low">
              {t('change_password.eyebrow')}
            </p>
            <h3 id="ops-change-password-title" className="m-0 mt-2 text-title text-paper">
              {t('change_password.title')}
            </h3>
          </div>
          <IconButton size="sm" onClick={onClose} disabled={submitting} label={t('common.close')}>
            <X aria-hidden="true" className="h-4 w-4" />
          </IconButton>
        </div>
        <p className="m-0 mt-5 text-body text-paper-mid">{t('change_password.intro')}</p>
        <div className="mt-5 flex flex-col gap-4">
          <FormField label={t('change_password.current')} fieldId="ops-change-password-current">
            <Input
              id="ops-change-password-current"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.currentTarget.value)}
              disabled={submitting}
              required
              data-testid="ops-change-password-current"
            />
          </FormField>
          <FormField label={t('change_password.next')} fieldId="ops-change-password-next">
            <Input
              id="ops-change-password-next"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.currentTarget.value)}
              disabled={submitting}
              required
              data-testid="ops-change-password-next"
            />
          </FormField>
          <FormField label={t('change_password.confirm')} fieldId="ops-change-password-confirm">
            <Input
              id="ops-change-password-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.currentTarget.value)}
              disabled={submitting}
              required
              data-testid="ops-change-password-confirm"
            />
          </FormField>
        </div>
        {error !== null ? (
          <p
            className="m-0 mt-4 rounded-lg border border-bad-line bg-bad-dim px-3 py-2 text-label text-bad-bright"
            role="alert"
            data-testid="ops-change-password-error"
          >
            {error}
          </p>
        ) : null}
        <div className="mt-6 flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="ops-change-password-cancel"
          >
            {t('change_password.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting}
            data-testid="ops-change-password-submit"
          >
            {submitting ? t('change_password.submitting') : t('change_password.submit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
